/* ============================================================
   LB-THEME.JS — Core theme JS
   ============================================================ */
'use strict';

/* ── Loader ──────────────────────────────────────────────── */
const Loader = {
  el:      null,
  logo:    null,
  fill:    null,
  curtain: null,
  _revealed: false,

  init() {
    this.el      = document.getElementById('lb-loader');
    this.logo    = document.getElementById('lb-loader-logo');
    this.fill    = document.getElementById('lb-progress-fill');
    this.curtain = document.getElementById('lb-curtain');
    if (!this.el) return;

    this._animateProgress();
    window.addEventListener('load', () => this._reveal());
    setTimeout(() => this._reveal(), 5000);
  },

  _ease(t) {
    return t < 0.5 ? 8*t*t*t*t : 1 - Math.pow(-2*t+2, 4)/2;
  },

  _animateProgress() {
    const start = performance.now();
    const dur   = 2000; // 2s au lieu de 1.2s
    const tick  = (now) => {
      const t = Math.min((now - start) / dur, 1);
      if (this.fill) this.fill.style.right = (1 - this._ease(t)) * 100 + '%';
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    setTimeout(() => this.logo?.classList.add('fade'), 1800);
  },

  _reveal() {
    if (this._revealed) return;
    this._revealed = true;
    this.curtain?.classList.add('reveal');
    // .36s delay + 1.1s animation + .5s fade = ~2s
    setTimeout(() => this.el?.classList.add('done'), 2000);
  }
};

/* ── Animate on scroll ───────────────────────────────────── */
const AOS = {
  init() {
    const els = document.querySelectorAll('.anim');
    if (!els.length) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });
    els.forEach(el => obs.observe(el));
  }
};

/* ── Sticky header ───────────────────────────────────────── */
const StickyHeader = {
  init() {
    const h = document.querySelector('.site-header.sticky');
    if (!h) return;
    window.addEventListener('scroll', () => {
      h.classList.toggle('scrolled', window.scrollY > 50);
    }, { passive: true });
  }
};

/* ── Announcement bar close ──────────────────────────────── */
const AnnBar = {
  init() {
    document.querySelector('.ann-bar__close')?.addEventListener('click', () => {
      const bar = document.querySelector('.ann-bar');
      if (!bar) return;
      bar.style.transition = 'max-height .3s ease, opacity .3s ease';
      bar.style.maxHeight  = bar.offsetHeight + 'px';
      bar.style.overflow   = 'hidden';
      requestAnimationFrame(() => {
        bar.style.maxHeight = '0';
        bar.style.opacity   = '0';
      });
      setTimeout(() => bar.remove(), 350);
    });
  }
};

/* ── Mobile Nav ──────────────────────────────────────────── */
const MobileNav = {
  hamburger: null,
  nav:       null,
  init() {
    this.hamburger = document.querySelector('[data-hamburger]');
    this.nav       = document.querySelector('.mobile-nav');
    if (!this.hamburger || !this.nav) return;
    this.hamburger.addEventListener('click', () => this.toggle());
    // Close on backdrop or close button
    this.nav.querySelectorAll('[data-nav-close]').forEach(b => b.addEventListener('click', () => this.close()));
    // Close on outside click when open
    document.addEventListener('click', (e) => {
      if (this.nav.classList.contains('open') &&
          !this.nav.contains(e.target) &&
          !this.hamburger.contains(e.target)) {
        this.close();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.nav.classList.contains('open')) this.close();
    });
  },
  toggle() { this.nav.classList.contains('open') ? this.close() : this.open(); },
  open() {
    this.nav.classList.add('open');
    this.hamburger.classList.add('open');
    this.hamburger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Ensure mobile nav is above everything
    this.nav.style.zIndex = '9990';
  },
  close() {
    this.nav.classList.remove('open');
    this.hamburger.classList.remove('open');
    this.hamburger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
};

/* ── Inline Header Search ────────────────────────────────── */
const Search = {
  input: null,
  drop:  null,
  timer: null,
  init() {
    this.input = document.getElementById('header-search-input');
    this.drop  = document.getElementById('header-search-drop');
    if (!this.input) return;

    this.input.addEventListener('input',   () => this._onInput());
    this.input.addEventListener('focus',   () => { if (this.input.value.trim().length >= 2) this._showDrop(); });
    this.input.addEventListener('keydown', e => {
      if (e.key === 'Escape') { this._hideDrop(); this.input.blur(); }
    });

    // Close on outside click
    document.addEventListener('click', e => {
      if (!this.input.closest('.header-search-form')?.contains(e.target)) this._hideDrop();
    });
  },
  _onInput() {
    const q = this.input.value.trim();
    clearTimeout(this.timer);
    if (q.length < 2) { this._hideDrop(); return; }
    this.timer = setTimeout(() => this._search(q), 280);
  },
  _showDrop() { this.drop?.classList.add('open'); },
  _hideDrop() { this.drop?.classList.remove('open'); },
  async _search(q) {
    if (!this.drop) return;
    this.drop.classList.add('open');
    this.drop.innerHTML = '<div class="hsd-loading"></div>';
    try {
      const r = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`);
      const d = await r.json();
      const products = d?.resources?.results?.products || [];
      if (!products.length) {
        this.drop.innerHTML = `<div class="hsd-empty">Aucun résultat pour "<strong>${q}</strong>"</div>`;
        return;
      }
      const fm = window.LB?.formatMoney || (c => (c/100).toFixed(2) + '€');
      const items = products.map(p => `
        <a href="${p.url}" class="hsd-item">
          ${p.featured_image?.url
            ? `<img src="${p.featured_image.url}" alt="${p.title}" class="hsd-img" loading="lazy">`
            : `<div class="hsd-img-ph"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="18" height="18"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>`}
          <div>
            <div class="hsd-title">${p.title}</div>
            <div class="hsd-price">${fm(p.price)}</div>
          </div>
        </a>`).join('');
      const footer = `<a href="/search?type=product&q=${encodeURIComponent(q)}" class="hsd-footer">Voir tous les résultats →</a>`;
      this.drop.innerHTML = items + footer;
    } catch(e) {
      this.drop.innerHTML = '<div class="hsd-empty">Erreur de recherche.</div>';
    }
  }
};

/* ── FAQ accordion ───────────────────────────────────────── */
const FAQ = {
  init() {
    document.querySelectorAll('.faq-q').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.faq-item');
        const isOpen = item.classList.contains('open');
        document.querySelectorAll('.faq-item.open').forEach(i => i.classList.remove('open'));
        if (!isOpen) item.classList.add('open');
        btn.setAttribute('aria-expanded', !isOpen);
      });
    });
  }
};

/* ── Filter groups ───────────────────────────────────────── */
const FilterGroups = {
  init() {
    document.querySelectorAll('.filter-group__h').forEach(h => {
      const group = h.closest('.filter-group');
      group.classList.add('open');
      h.addEventListener('click', () => group.classList.toggle('open'));
    });
  }
};

/* ── Product tabs ────────────────────────────────────────── */
const Tabs = {
  init() {
    document.querySelectorAll('.tabs-nav').forEach(nav => {
      nav.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const container = btn.closest('.prod-tabs');
          const target    = btn.dataset.tab;
          container.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
          container.querySelectorAll('.tab-pane').forEach(p => p.classList.remove('active'));
          btn.classList.add('active');
          container.querySelector('#' + target)?.classList.add('active');
        });
      });
    });
  }
};

/* ── Product gallery ─────────────────────────────────────── */
const Gallery = {
  init() {
    const main    = document.querySelector('.gallery__main');
    const mainImg = main?.querySelector('img');
    if (!main) return;
    // Thumbnails
    document.querySelectorAll('.gallery__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        document.querySelectorAll('.gallery__thumb').forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        if (mainImg && thumb.dataset.src) {
          mainImg.style.opacity = '0';
          mainImg.src = thumb.dataset.src;
          mainImg.onload = () => mainImg.style.opacity = '1';
        }
      });
    });
    // Zoom
    main.addEventListener('click', () => {
      main.classList.toggle('zoomed');
    });
    main.addEventListener('mousemove', e => {
      if (!main.classList.contains('zoomed') || !mainImg) return;
      const r = main.getBoundingClientRect();
      mainImg.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
    });
  }
};

/* ── Init all ────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  Loader.init();
  AOS.init();
  StickyHeader.init();
  AnnBar.init();
  MobileNav.init();
  Search.init();
  FAQ.init();
  FilterGroups.init();
  Tabs.init();
  Gallery.init();
});
